import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PercentageIncomePerState } from '@/reducers/auth/userReducer'
import { fetchIfNeededWrapper, fetchWrapper } from '@/reducers/fetch'

import {
  FILING_STATUSES,
  TAX_ENTITY_TYPES,
} from '@/features/taxes/taxConstants'
import { UserTaxEstimate } from '@/features/taxes/quarterly/userTaxEstimates.slice'
import { keyBy, uniq } from 'lodash'
import { adminApiAxios, apiAxios } from '@/api'
import {
  buildUrlWithOffsetPagination,
  OffsetPaginatedResponse,
  PrismaOffsetPagination,
} from '@/utils/fetchHelpers'

export enum UserTaxCalculationStatuses {
  notStarted = 'not_started',
  calculated = 'calculated',
  savedAndNotified = 'saved_and_notified',
}
export enum UserTaxCalculationTabNames {
  all = 'All Customers',
  singleState = 'Single State Filings',
  multiState = 'Multi State Filings',
  edgeCases = 'Edge Cases',
}

export interface CalculatedStateEstimate {
  state: string
  value?: number
}

export interface UserTaxCalculationMembership {
  status: string
  startDate?: string | null
  trialStartDate?: string | null
  trialEndDate?: string | null
}

export interface UserTaxCalculationUser {
  createdAt: string
  memberships: UserTaxCalculationMembership[]
}

export interface AboundResponse {
  data: {
    '1099Income': number
    effectiveTaxRate: number
    expenseDeduction: number
    federalIncomeTax: number
    federalTaxOutstanding: number
    federalTaxTotal: number
    filingState: string
    filingStatus: string
    irsPayments: number
    quarterlyPayments: number
    marginalTaxRate: number
    medicareTax: number
    mileage: number
    mileageDeduction: number
    qbiDeduction: number
    selfEmploymentTax: number
    smartTaxRate: number
    socialSecurityTax: number
    stateIncomeTax: number
    stateTaxPayments: number
    taxBalance: number
    taxWithholdings: number
    taxWithholdingsPending: number
    taxTotalOutstanding: number
    totalTax: number
    w2Income: number
    year: number
    annualFederalIncomeTaxLiabilityInCents: number | null
    annualStateTaxLiabilityInCents: number | null
    projectedFederalWithholdingInCents?: number
    projectedStateWithholdingInCents?: number
    projectedSpouseFederalWithholdingInCents?: number
    projectedSpouseStateWithholdingInCents?: number
  }
  request: {
    timestamp: string
    requestId: string
  }
}

export const isAboundResponse = (
  taxCalculationOutput: unknown
): taxCalculationOutput is AboundResponse => {
  //Verify the format is a valid object
  if (!taxCalculationOutput || typeof taxCalculationOutput !== 'object')
    return false

  return (
    'federalTaxOutstanding' in taxCalculationOutput &&
    'stateIncomeTax' in taxCalculationOutput
  )
}

export interface MultiStateSecondaryStateEstimate {
  state: string
  percentage: number
  annualApportionedIncomeInCents: number
  annualStateTaxLiabilityInCents: number
  quarterStateEstimateInCents: number
  stateStandardDeductionInCents?: number
  annualStateTaxableIncomeInCents?: number
  YTDGrossStateTaxLiabilityInCents?: number
  creditedPaymentsInCents?: number
  allQuarterPaymentsLogged?: boolean
}

export interface TaxCalculationOutput {
  data: {
    year: string
    stateIncomeTax: number
    selfEmploymentTax: number
    federalTaxOutstanding: number
    annualGrossIncomeInCents: number
    annualTaxableIncomeInCents: number
    annualFederalIncomeTaxLiabilityInCents: number | null
    annualStateTaxLiabilityInCents: number | null
    annualSelfEmploymentTaxLiabilityInCents: number
    annualQBIDeductionInCents: number
    annualTotalFederalWithholdingInCents: number
    annualTotalStateWithholdingInCents: number
    annualFederalEstimateInCents: number
    annualStateEstimateInCents: number
    quarterFederalEstimateInCents: number
    quarterStateEstimateInCents: number
    YTDGrossFederalTaxLiabilityInCents: number
    YTDGrossStateTaxLiabilityInCents: number
    YTDFederalTaxPaymentsMadeInCents: number
    YTDStateTaxPaymentsMadeInCents: number
    allFederalQuarterPaymentsLogged: boolean
    allStateQuarterPaymentsLogged: boolean
    annualSpouseSelfEmploymentTaxLiabilityInCents?: number
    federalStandardDeductionInCents: number
    deductibleSelfEmploymentTaxInCents: number
    projectedFederalWithholdingInCents: number
    projectedStateWithholdingInCents: number
    projectedSpouseFederalWithholdingInCents: number
    projectedSpouseStateWithholdingInCents: number
    stateStandardDeductionInCents?: number
    annualStateTaxableIncomeInCents?: number
    annualSelfEmploymentIncomeInCents?: number
  }
  multiState?: {
    secondaryStates: MultiStateSecondaryStateEstimate[]
    apportionmentBaseIncomeInCents?: number
  }
}

export const isTaxCalculationOutput = (
  taxCalculationOutput: unknown
): taxCalculationOutput is TaxCalculationOutput => {
  //Verify the format is a valid object
  if (!taxCalculationOutput || typeof taxCalculationOutput !== 'object')
    return false

  return (
    'data' in taxCalculationOutput &&
    typeof taxCalculationOutput.data === 'object' &&
    taxCalculationOutput.data !== null &&
    'quarterFederalEstimateInCents' in taxCalculationOutput.data &&
    'quarterStateEstimateInCents' in taxCalculationOutput.data
  )
}

export interface UserTaxCalculation {
  id: number
  status: UserTaxCalculationStatuses
  calendarYear: string
  calendarQuarter: string
  createdAt: string
  updatedAt: string
  deletedAt?: string | null
  userId: number
  userUuid: string
  firstName: string
  lastName: string
  stateQuarterlyEstimates: CalculatedStateEstimate[]
  calculatedByUserId?: number | null
  //we need to keep AboundResponse because the format of legacy data is different
  taxCalculationOutput?: AboundResponse | TaxCalculationOutput
  calculatedAt?: string
  filingStates: string[]
  filingStatus: FILING_STATUSES
  taxEntityType?: TAX_ENTITY_TYPES
  homeState: string
  percentageIncomePerState?: PercentageIncomePerState[]
  taxProfileInputsUpdatedAt: string
  plInputsUpdatedAt?: string
  notifiedAt?: string
  notes?: string | null
  federalQuarterlyEstimateInCents: number
  wIncomeInCents: number | null
  spouseWIncomeInCents: number | null
  totalWIncomeInCents: number
  federalWithholdingInCents: number
  stateWithholdingInCents: number
  spouseFederalWithholdingInCents: number
  spouseStateWithholdingInCents: number
  projectedFederalWithholdingInCents: number
  projectedStateWithholdingInCents: number
  projectedSpouseFederalWithholdingInCents: number
  projectedSpouseStateWithholdingInCents: number
  totalFederalWithholdingInCents: number
  totalStateWithholdingInCents: number
  periodIncomeInCents: number
  periodExpensesInCents: number
  annualizedIncomeInCents: number
  annualizedExpensesInCents: number
  annualizedNetIncomeInCents: number
  paidEstimatesAtQ4Calculation?: UserTaxEstimate[] | null
  relocatedPreviousStateAt: string | null
  scorpPracticeWIncomeInCents: number | null
  otherIndividualIncomeInCents: number | null
  spouseIndividualIncomeInCents: number | null
  annualizedTotalWIncomeInCents: number | null
  isSingleState: boolean | null
  taxProfileLastReviewedAt: string | null
  booksClosedForPreviousMonths: boolean | null
  hasPaidAllTaxEstimates: boolean | null
  unseenEstimate: boolean | null
  needsRecalculation: boolean | null
  annualizedSpouseWIncomeInCents: number | null
  annualizedScorpPracticeWIncomeInCents: number | null
  annualizedWIncomeInCents: number | null
  estimatedAnnualW2IncomeInCents: number | null
  estimatedAnnualSpouseW2IncomeInCents: number | null
  estimatedAnnualScorpPracticeW2IncomeInCents: number | null
}

export interface AdminUserTaxCalculation extends UserTaxCalculation {
  user: UserTaxCalculationUser
}

interface ReleaseUserTaxCalculationsModel {
  ids: number[]
}

export interface UserTaxCalculationsState {
  byId: { [key: string]: UserTaxCalculation }
  allIds: string[]
}

export interface TabCounts {
  all: number
  singleState: number
  multiState: number
  edgeCases: number
}

export type SortPreset =
  | 'default'
  | 'userNameAsc'
  | 'userNameDesc'
  | 'taxProfileAsc'
  | 'taxProfileDesc'

export interface PaginatedCalculationsResponse extends OffsetPaginatedResponse<AdminUserTaxCalculation> {
  // Quarter-level tab counts exclude table-only filters like userSearch
  // and readyToCalculate. Use pagination.totalItems for filtered table size.
  counts: TabCounts
}

export const USER_TAX_CALCULATION_TAB_TO_API_PARAM: Record<
  UserTaxCalculationTabNames,
  keyof TabCounts
> = {
  [UserTaxCalculationTabNames.all]: 'all',
  [UserTaxCalculationTabNames.singleState]: 'singleState',
  [UserTaxCalculationTabNames.multiState]: 'multiState',
  [UserTaxCalculationTabNames.edgeCases]: 'edgeCases',
}

const initialState: UserTaxCalculationsState = {
  byId: {},
  allIds: [],
}

const userTaxCalculationsSlice = createSlice({
  name: 'userTaxCalculations',
  initialState,
  reducers: {
    receiveAllUserTaxCalculations: (
      state,
      action: PayloadAction<{ [key: string]: UserTaxCalculation }>
    ) => {
      state.byId = {
        ...state.byId,
        ...action.payload,
      }

      state.allIds = uniq([
        ...state.allIds,
        ...Object.values(action.payload).map((c) => c.id.toString()),
      ])
    },
    receiveSingleUserTaxCalculation: (
      state,
      action: PayloadAction<UserTaxCalculation>
    ) => {
      state.byId[action.payload.id] = action.payload
      state.allIds = uniq([...state.allIds, action.payload.id.toString()])
    },
  },
})

export default userTaxCalculationsSlice.reducer

export const {
  receiveAllUserTaxCalculations,
  receiveSingleUserTaxCalculation,
} = userTaxCalculationsSlice.actions

export const FETCH_ALL_USER_TAX_CALCULATIONS_KEY =
  'FETCH_ALL_USER_TAX_CALCULATIONS_KEY'
export const UPDATE_SINGLE_USER_TAX_CALCULATIONS_KEY =
  'UPDATE_SINGLE_USER_TAX_CALCULATIONS_KEY'
export const RELEASE_USER_TAX_CALCULATIONS_KEY =
  'RELEASE_USER_TAX_CALCULATIONS_KEY'

export const fetchUserTaxCalculationsIfNeeded = (alwaysFetch?: boolean) =>
  fetchIfNeededWrapper({
    alwaysFetch,
    fetchKey: FETCH_ALL_USER_TAX_CALCULATIONS_KEY,
    defaultErrorMessage: 'Error fetching all user tax calculations',
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.get<UserTaxCalculation[]>(
        '/finances/api/v1/user_tax_calculations'
      )

      dispatch(receiveAllUserTaxCalculations(keyBy(json.data, 'id')))

      return json.data
    },
  })

export const UPDATE_USER_TAX_CALCULATIONS_KEY =
  'UPDATE_USER_TAX_CALCULATIONS_KEY'
export const updateUserTaxCalculation = (
  id: number,
  data: Partial<UserTaxCalculation>
) =>
  fetchWrapper({
    fetchKey: UPDATE_USER_TAX_CALCULATIONS_KEY,
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.patch<UserTaxCalculation>(
        `/finances/api/v1/user_tax_calculations/${id}`,
        data
      )

      dispatch(receiveSingleUserTaxCalculation(json.data))

      return json.data
    },
  })

export const RECALCULATE_USER_TAX_CALCULATIONS_KEY =
  'RECALCULATE_USER_TAX_CALCULATIONS_KEY'
export const recalculateUserTaxCalculation = () =>
  fetchWrapper({
    fetchKey: RECALCULATE_USER_TAX_CALCULATIONS_KEY,
    fetchFunction: async () => {
      await apiAxios.post<undefined>(
        '/finances/api/v1/user_tax_calculations/recalculateCurrentQuarter'
      )

      return true
    },
  })

// TODO(ENG-1153): Remove Redux dispatch — response data is already available via
// useOffsetPaginatedQuery and doesn't need to round-trip through the store.
// Table components should receive data via props instead of selectors.
export const adminFetchUserTaxCalculations = (
  pagination: PrismaOffsetPagination,
  params: {
    calendarYear: string
    calendarQuarter: string
    tab: keyof TabCounts
    sortBy?: SortPreset
    userSearch?: string
    readyToCalculate?: boolean
  }
) =>
  fetchWrapper({
    shouldHandleError: false,
    fetchFunction: async (dispatch) => {
      const queryParts = [
        `&calendarYear=${params.calendarYear}`,
        `&calendarQuarter=${params.calendarQuarter}`,
        `&tab=${params.tab}`,
        params.sortBy ? `&sortBy=${params.sortBy}` : '',
        params.userSearch
          ? `&userSearch=${encodeURIComponent(params.userSearch)}`
          : '',
        params.readyToCalculate ? '&readyToCalculate=true' : '',
      ].join('')

      const url = buildUrlWithOffsetPagination({
        fetchUrlBase: '/finances/api/v1/admin/user_tax_calculations/paginated',
        pagination,
        additionalQueryParams: queryParts,
      })

      const json = await adminApiAxios.get<PaginatedCalculationsResponse>(url)
      dispatch(receiveAllUserTaxCalculations(keyBy(json.data.data, 'id')))

      return json.data
    },
  })

export const updateSingleUserTaxCalculation = (
  id: number,
  data: Partial<UserTaxCalculation>
) =>
  fetchWrapper({
    fetchKey: UPDATE_SINGLE_USER_TAX_CALCULATIONS_KEY + id,
    defaultErrorMessage: 'Error updating the tax calculation',
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.put<UserTaxCalculation>(
        `/finances/api/v1/admin/user_tax_calculations/${id}`,
        data
      )

      dispatch(receiveSingleUserTaxCalculation(json.data))

      return json.data
    },
  })

export const releaseUserTaxCalculations = (
  model: ReleaseUserTaxCalculationsModel
) =>
  fetchWrapper({
    // Reusing key so our component doesn't need to have multiple vars for monitoring error & loading
    fetchKey: RELEASE_USER_TAX_CALCULATIONS_KEY,
    defaultErrorMessage: 'Error releasing user tax calculations',
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.post<UserTaxCalculation[]>(
        '/finances/api/v1/admin/user_tax_calculations/release',
        model
      )

      dispatch(receiveAllUserTaxCalculations(keyBy(json.data, 'id')))

      return json.data
    },
  })
